import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { ConfigProvider, List, Button, Select, Input, Typography, message, Tabs, Modal, Spin, Row, Col, Card, Statistic, Space, Divider, Badge, Tooltip, Switch, Upload, Form, Image, Empty, Alert, Descriptions, DatePicker, Collapse, Carousel, Dropdown, Menu } from 'antd';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './RequestsPage.css';
import ClientRequestForm from '../../components/ClientRequestForm';
import api from '../../authentication/axiosInstance';
import ClientRequestsPortal from '../../components/ClientRequestsPortal';
import { AuthContext } from '../../context/AuthContext';
import { CalendarOutlined, EnvironmentOutlined, InfoCircleOutlined, MailOutlined, UserOutlined, PhoneOutlined, ClockCircleOutlined, UploadOutlined, EditOutlined, SaveOutlined, PictureOutlined, PlusOutlined, LeftOutlined, RightOutlined, FileTextOutlined, DownloadOutlined, DownOutlined, CheckCircleOutlined } from '@ant-design/icons';
import SnowEdgeRequestForm from '../../components/SnowEdgeRequestForm';
import axios from 'axios';
import moment from 'moment';

import DispatchRequestsTable from '../../components/DispatchRequestsTable';

const { Option } = Select;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const getDispatchStatusColor = (status) => {
  switch(status) {
    case 'unread':
      return 'warning';
    case 'read':
      return 'processing';
    case 'confirmed':
      return 'success';
    case 'completed':
      return 'default';
    default:
      return 'default';
  }
};

const RequestLocationMap = React.memo(({ request, requestType }) => {
  const lat = requestType === 'regular' ? request.latitude : (request.site && request.site.latitude);
  const lon = requestType === 'regular' ? request.longitude : (request.site && request.site.longitude);
  
  const address = requestType === 'regular' 
    ? request.address 
    : (request.site && request.site.address) 
      ? request.site.address 
      : 'Location';
  
  if (!lat || !lon) {
    return <Empty description="No location data available" />;
  }
  
  return (
    <MapContainer 
      center={[lat, lon]} 
      zoom={14} 
      style={{ height: '200px', width: '100%', borderRadius: '4px' }}
      key={`${lat}-${lon}`} // Force re-render when location changes
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <Marker position={[lat, lon]}>
        <Popup>
          {address}
        </Popup>
      </Marker>
    </MapContainer>
  );
});

const RequestsPage = () => {
  const { userRole, userEmail, hasSnowEdgeAccess, hasRegularRequestAccess } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(() => {
    // Set initial tab based on role and permissions
    if (userRole !== 'client') return '1'; // Admin/staff sees View Requests tab
    if (hasRegularRequestAccess) return '2'; // Regular request form
    if (hasSnowEdgeAccess) return '3'; // SnowEdge request form
    return '4'; // Default to Client Requests tab
  });
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [map, setMap] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewRequestOpen, setViewRequestOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestType, setRequestType] = useState('regular');
  const [snowEdgeRequests, setSnowEdgeRequests] = useState([]);
  const [statusNotes, setStatusNotes] = useState('');
  const [statusPicture, setStatusPicture] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedStatusNotes, setEditedStatusNotes] = useState('');
  const [imageKey, setImageKey] = useState(0);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [claimNotes, setClaimNotes] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedRequestReceiver, setSelectedRequestReceiver] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [activeDispatch, setActiveDispatch] = useState(null);
  const [activeDispatches, setActiveDispatches] = useState([]);
  const [showDispatchForm, setShowDispatchForm] = useState(false);
  const [staffSearchValue, setStaffSearchValue] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingDispatch, setLoadingDispatch] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [openRequest, setOpenRequest] = useState(null);
  
  const [clientFilter, setClientFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(['pending', 'claimed', 'completed']);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [generatingReport, setGeneratingReport] = useState(false);

  const [clientDropdownVisible, setClientDropdownVisible] = useState(false);
  const [typeDropdownVisible, setTypeDropdownVisible] = useState(false);
  const [statusDropdownVisible, setStatusDropdownVisible] = useState(false);
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);

  // Form reference for dispatch form
  const [form] = Form.useForm();

  const [activeDispatcher, setActiveDispatcher] = useState(null);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/');
      console.log('Fetched clients (raw):', response.data);
      // Make sure clients have proper client_id properties
      const processedClients = response.data.map(client => {
        if (!client.client_id && client.id) {
          return { ...client, client_id: client.id };
        }
        return client;
      });
      console.log('Processed clients:', processedClients);
      setClients(processedClients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      message.error('Failed to fetch clients');
    }
  };

  useEffect(() => {
    fetchClients();
    
    // Debug clients after fetch
    setTimeout(() => {
      console.log('Clients after fetch:', clients);
    }, 1000);
  }, []);

  const customMarkerIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    shadowSize: [41, 41],
  });

  const getMapboxUrl = () => {
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=${accessToken}`;
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error('Please select a file first');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('files[]', file);
    });

    formData.forEach((file) => {
      console.log(file)
      api.post(`/request-manager/requests/${openRequest.id}/photo-upload-signed-url`)
      .then(function (result) {
        var signedUrl = result.data.signed_url;
        var options = {
          headers: {
            'Content-Type': "image/jpeg"
          }
        };
        return axios.put(signedUrl, file, options);
      })
      .then(function (result) {
        console.log(result);
        setFileList([]);
        message.success('upload successfully.');
        fetchRequest(openRequest.id)
      })
      .catch(function (err) {
        console.log(err);
        message.error('upload failed.');
      })
      .finally(() => {
        setUploading(false);
      });
    })
  };

  const props = {
    onRemove: file => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: file => {
      setFileList([...fileList, file]);
      return false;
    },
    fileList,
  };

  useEffect(() => {
    console.log('User Role:', userRole);
    console.log('User Email:', userEmail);
    if (userRole !== 'client') {
      // Only fetch staff members if user is not a client
      fetchStaffMembers();
    }
    fetchRequests();
    fetchSnowEdgeRequests();
  }, [userRole, userEmail]);

  // Add debug function to inspect snowEdge requests
  useEffect(() => {
    if (snowEdgeRequests.length > 0) {
      console.log('SnowEdge Request Structure (first item):', snowEdgeRequests[0]);
      console.log('Total SnowEdge Requests:', snowEdgeRequests.length);
      console.log('SnowEdge statuses:', [...new Set(snowEdgeRequests.map(r => r.status))]);
    }
  }, [snowEdgeRequests]);

  useEffect(() => {
    if (selectedRequest && selectedRequest.status_pictures) {
      setImageUrl(selectedRequest.status_pictures);
      setImageError(false);
    } else {
      setImageUrl(null);
    }
  }, [selectedRequest]);

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      const response = await api.get('/request-manager/requests/');
      
      if (response.status === 200) {
        setRequests(response.data);
      } else {
        message.error('Failed to fetch requests');
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      message.error('Failed to fetch requests');
    } finally {
      setRequestsLoading(false);
    }
  };
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get('/api/users/');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  const fetchRequest = useCallback(async (requestId) => {
    try {
      let response;
      if (requestType === 'regular') {
        response = await api.get(`/request-manager/requests/${requestId}/`);
      } else {
        response = await api.get(`/request-manager/requests/${requestId}/`);
      }
      
      if (response.status === 200) {
        const request = response.data;
        
        // Update only the necessary state
        setOpenRequest(request);
        setSelectedRequest(request);
        
        // Don't update the entire requests list, only this specific one
        if (requestType === 'regular') {
          setRequests(prevRequests => 
            prevRequests.map(req => req.id === requestId ? request : req)
          );
        } else {
          setSnowEdgeRequests(prevRequests => 
            prevRequests.map(req => req.id === requestId ? request : req)
          );
        }
        
        // Set notes states only if viewing the modal
        if (viewRequestOpen) {
          if (request.claimed_notes) {
            setClaimNotes(request.claimed_notes);
          }
          
          if (request.completed_notes) {
            setCompleteNotes(request.completed_notes);
          }
        }
        
        return request;
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      message.error('Failed to fetch request details');
      return null;
    }
  }, [requestType, viewRequestOpen]);

  const fetchSnowEdgeRequests = async () => {
    try {
      setRequestsLoading(true);
      const response = await api.get('/request-manager/snow-edge-requests/');
      
      if (response.status === 200) {
        setSnowEdgeRequests(response.data);
      } else {
        message.error('Failed to fetch SnowEdge requests');
      }
    } catch (error) {
      console.error('Failed to fetch SnowEdge requests:', error);
      message.error('Failed to fetch SnowEdge requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const response = await api.get('/staff/');
      if (response.data) {
        setStaffMembers(response.data);
        // We'll fetch the active dispatcher after fetching staff members
        fetchActiveDispatcher();
      }
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      // Don't show error message to user for this
    }
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      salting_lots: "Salting Lots",
      plowing_lots: "Plowing Lots",
      emergency_exits: "Emergency Exits",
      loading_docks: "Loading Docks",
      walkways: "Walkways",
      other: "Other",
    };
    return labels[type] || type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleRequestClick = useCallback((requestId) => {
    // Find the request in the existing state rather than fetching it again
    const request = requestType === 'regular'
      ? requests.find(r => r.id === requestId)
      : snowEdgeRequests.find(r => r.id === requestId);
      
    if (request) {
      setSelectedRequest(request);
      setOpenRequest(request);
      setViewRequestOpen(true);
      setImageKey(prevKey => prevKey + 1);  // Force re-render of image
      setClaimNotes(request.claimed_notes || '');
      setCompleteNotes(request.completed_notes || '');
      setDispatchNotes('');
      
      // Reset form values
      form.resetFields();

      // Navigate map if coordinates exist
      if (map) {
        const lat = requestType === 'regular' ? request.latitude : (request.site && request.site.latitude);
        const lon = requestType === 'regular' ? request.longitude : (request.site && request.site.longitude);
        if (lat && lon) {
          map.flyTo([lat, lon], 14);
        }
      }

      // Only fetch active dispatches if request is claimed to avoid unnecessary API calls
      if (request.status === 'claimed') {
        fetchActiveDispatches(requestId);
      } else {
        setActiveDispatches([]);
      }
    }
  }, [requestType, requests, snowEdgeRequests, map, form]);

  // Separate function to fetch active dispatches
  const fetchActiveDispatches = async (requestId) => {
    try {
      const response = await api.get('/request-manager/request-dispatches/', {
        params: {
          [requestType === 'regular' ? 'regular_request' : 'snowedge_request']: requestId,
          status: ['unread', 'read', 'confirmed', 'completed'] // Get all dispatches for this request
        }
      });
      
      setActiveDispatches(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch dispatches:', error);
      setActiveDispatches([]);
    }
  };

  const closeModal = () => {
    setViewRequestOpen(false);
    setSelectedRequest(null);
    setOpenRequest(null);
  };

  const handleClaimRequest = async (e, requestId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const formData = new FormData();
      formData.append('status', 'claimed');
      formData.append('notes', claimNotes);
      if (statusPicture) {
        formData.append('status_picture', statusPicture);
      }

      const response = await api.post(`/request-manager/requests/${requestId}/update_status/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        message.success('Request Claimed Successfully');
        setActiveRequestId(null);
        setClaimNotes('');
        setStatusPicture(null);
        // Update the local state
        setRequests(prevRequests =>
          prevRequests.map(req =>
            req.id === requestId ? { ...req, status: 'claimed', claimed_notes: claimNotes } : req
          )
        );
        setSelectedRequest(req => ({ ...req, status: 'claimed', claimed_notes: claimNotes }));
      }
    } catch (error) {
      console.error('Error claiming request:', error);
      if (error.response && error.response.data && error.response.data.error) {
        message.error(`Failed to claim request: ${error.response.data.error}`);
      } else {
        message.error('Error claiming request');
      }
    }
  };

  const handleCompleteRequest = async (e, requestId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const formData = new FormData();
      formData.append('status', 'completed');
      formData.append('notes', completeNotes);
      if (statusPicture) {
        formData.append('status_picture', statusPicture);
      }

      const response = await api.post(`/request-manager/requests/${requestId}/update_status/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        message.success('Request Completed Successfully');
        setActiveRequestId(null);
        setCompleteNotes('');
        setStatusPicture(null);
        // Update the local state
        setRequests(prevRequests =>
          prevRequests.map(req =>
            req.id === requestId ? { ...req, status: 'completed', completed_notes: completeNotes } : req
          )
        );
        closeModal();
      }
    } catch (error) {
      console.error('Error completing request:', error);
      message.error('Error completing request');
    }
  };

  const handleApproveSnowEdgeRequest = async (requestId) => {
    try {
      const formData = new FormData();
      formData.append('status', 'approved');
      
      // For SnowEdge requests, use the general requests endpoint with the ID
      const response = await api.patch(`/request-manager/requests/${requestId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        // Update request in local state
        setSnowEdgeRequests(prevRequests =>
          prevRequests.map(req =>
            req.id === requestId ? { ...req, status: 'approved' } : req
          )
        );

        // If this is the selected request, update it too
        if (selectedRequest && selectedRequest.id === requestId) {
          setSelectedRequest(prevSelected => ({
            ...prevSelected,
            status: 'approved',
          }));
        }

        message.success('SnowEdge request approved successfully');
      } else {
        message.error('Error approving SnowEdge request');
      }
    } catch (error) {
      console.error('Error approving SnowEdge request:', error);
      message.error('Error approving SnowEdge request');
    }
  };
  
  // Staff search filtering
  const staffSearch = value => {
    setStaffSearchValue(value);
  };

  const filteredStaff = useMemo(() => {
    if (!staffSearchValue || staffSearchValue.trim() === '') {
      return staffMembers;
    }
    
    const searchLower = staffSearchValue.toLowerCase();
    return staffMembers.filter(
      s => s.name.toLowerCase().includes(searchLower) || 
           (s.email && s.email.toLowerCase().includes(searchLower))
    );
  }, [staffMembers, staffSearchValue]);

  const handleEditNotes = () => {
    setIsEditingNotes(true);
    setEditedStatusNotes(selectedRequest.status_notes || '');
  };

  const handleSaveNotes = async () => {
    try {
      const formData = new FormData();
      formData.append('status_notes', editedStatusNotes);
      if (statusPicture) {
        formData.append('status_picture', statusPicture);
      }

      const response = await api.patch(`/request-manager/requests/${selectedRequest.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        // Update the local state
        setRequests(prevRequests =>
          prevRequests.map(req =>
            req.id === selectedRequest.id ? { ...req, status_notes: editedStatusNotes } : req
          )
        );
        setSelectedRequest({ ...selectedRequest, status_notes: editedStatusNotes });
        setIsEditingNotes(false);
        setStatusPicture(null);
        message.success('Status notes and pictures updated successfully');
      } else {
        throw new Error('Failed to update status notes');
      }
    } catch (error) {
      console.error('Error updating status notes and pictures:', error);
      message.error('Failed to update status notes and pictures');
    }
  };

  const handleRequestReceiverChange = async (value) => {
    try {
      await api.post(`/staff/${value}/set_request_receiver/`);
      setSelectedRequestReceiver(value);
      message.success('Request receiver updated successfully');
    } catch (error) {
      console.error('Failed to update request receiver:', error);
      message.error('Failed to update request receiver');
    }
  };

  const handleCreateDispatch = async () => {
    setLoadingDispatch(true);
    try {
      if (!selectedStaff) {
        message.error('Please select a staff member');
        return;
      }

      const dispatchData = {
        staff: selectedStaff,
        notes: dispatchNotes,
        [requestType === 'regular' ? 'regular_request' : 'snowedge_request']: selectedRequest.id
      };

      const response = await api.post('/request-manager/request-dispatches/', dispatchData);
      message.success('Dispatch created successfully');
      
      // Refresh the active dispatches
      const updatedDispatches = await api.get('/request-manager/request-dispatches/', {
        params: {
          [requestType === 'regular' ? 'regular_request' : 'snowedge_request']: selectedRequest.id,
          status: ['unread', 'read', 'confirmed']
        }
      });
      setActiveDispatches(updatedDispatches.data);
      
      // Reset form
      setSelectedStaff(null);
      setDispatchNotes('');
    } catch (error) {
      console.error('Failed to create dispatch:', error);
      message.error('Failed to create dispatch');
    } finally {
      setLoadingDispatch(false);
    }
  };

  const handleCompleteDispatch = async (e, dispatchId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      await api.post(`/request-manager/request-dispatches/${dispatchId}/update_status/`, {
        status: 'completed',
        response_notes: 'Dispatch completed successfully'
      });
      
      // Refresh active dispatch
      const response = await api.get(`/request-manager/request-dispatches/${dispatchId}/`);
      setActiveDispatch(response.data);
      message.success('Dispatch completed successfully');
    } catch (error) {
      message.error('Failed to complete dispatch');
    }
  };

  const handleGenerateReport = async (clientId) => {
    if (!clientId) {
      message.warning('Please select a client first');
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await api.get(`/request-manager/client/${clientId}/report/`, {
        responseType: 'blob', // Important for handling PDF response
      });

      // Create a blob from the PDF stream
      const file = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a link element and trigger download
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1].replace(/"/g, '')
        : 'client_requests.pdf';
      link.click();

      message.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDispatchRequest = async (staffId) => {
    if (!selectedRequest) {
      message.error('No request selected');
      return;
    }
    
    setLoadingDispatch(true);
    try {
      const payload = {
        staff: staffId,
        notes: dispatchNotes,
      };
      
      // Add the appropriate request field based on request type
      if (requestType === 'regular') {
        payload.regular_request = selectedRequest.id;
      } else {
        payload.snowedge_request = selectedRequest.id;
      }
      
      const response = await api.post('/request-manager/request-dispatches/', payload);
      
      if (response.status === 201) {
        message.success('Request dispatched successfully');
        setDispatchNotes('');
        
        // Add the new dispatch to the active dispatches
        setActiveDispatches(prev => [response.data, ...prev]);
      } else {
        message.error('Failed to dispatch request');
      }
    } catch (error) {
      console.error('Error dispatching request:', error);
      message.error('Error dispatching request');
    } finally {
      setLoadingDispatch(false);
    }
  };

  // Add this helper function for client access
  const getClientIdFromRequest = (request) => {
    if (!request || !request.site) return null;
    
    // Log the request structure for debugging
    console.log('Request structure for client ID check:', request.id, {
      'site': request.site,
      'site.client': request?.site?.client,
      'site.client_id': request?.site?.client_id,
      'site.client_name': request?.site?.client_name
    });
    
    // Try different potential paths to client ID
    if (request.site.client_id) return request.site.client_id;
    if (request.site.client && typeof request.site.client === 'object') {
      return request.site.client.client_id || request.site.client.id;
    }
    if (request.site.client && typeof request.site.client === 'string') {
      return request.site.client;
    }
    
    return null;
  };

  // Also add helper to get client name
  const getClientNameFromRequest = (request, clientsList) => {
    if (!request || !request.site) return 'Unknown';
    
    // If the site has a client_name property, use it
    if (request.site.client_name) return request.site.client_name;
    
    // Otherwise try to find the client in the clients list
    const clientId = getClientIdFromRequest(request);
    if (clientId && clientsList && clientsList.length > 0) {
      const client = clientsList.find(c => c.client_id === clientId);
      return client ? client.client_name : 'Unknown';
    }
    
    return 'Unknown';
  };

  // Updated handleSortChange function to handle sort field and direction
  const handleSortChange = (key) => {
    if (key === sortBy) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different field, set it with default desc direction
      setSortBy(key);
      setSortDirection('desc');
    }
    setSortDropdownVisible(false);
  };

  const filteredRequests = useMemo(() => {
    // First filter the requests
    const filtered = requests
      .filter((request) => {
        if (clientFilter === null) return true;
        
        const requestClientId = getClientIdFromRequest(request);
        return requestClientId === clientFilter || String(requestClientId) === String(clientFilter);
      })
      .filter((request) => typeFilter === null || request.request_type === typeFilter)
      .filter((request) => {
        if (statusFilter === null) return true;
        if (Array.isArray(statusFilter)) {
          return statusFilter.includes(request.status);
        }
        return request.status === statusFilter;
      });
    
    // Then sort the filtered results
    return filtered.sort((a, b) => {
      let valueA, valueB;
      
      // Determine which fields to compare based on sortBy
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.created_at || 0);
          valueB = new Date(b.created_at || 0);
          break;
        case 'name':
          valueA = getClientNameFromRequest(a, clients) || '';
          valueB = getClientNameFromRequest(b, clients) || '';
          break;
        case 'status':
          valueA = a.status || '';
          valueB = b.status || '';
          break;
        default:
          // Default to date sorting
          valueA = new Date(a.created_at || 0);
          valueB = new Date(b.created_at || 0);
      }
      
      // Apply the sort direction
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      // Compare the values
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
  }, [requests, clientFilter, typeFilter, statusFilter, sortBy, sortDirection, clients]);

  // Add filtered SnowEdge requests with the same filtering logic
  const filteredSnowEdgeRequests = useMemo(() => {
    console.log('SnowEdge request count before filtering:', snowEdgeRequests.length);
    
    // If no filters are applied, return all snow edge requests
    if (clientFilter === null && statusFilter === null) {
      console.log('No filters applied, returning all snowEdgeRequests');
      return snowEdgeRequests;
    }
    
    // First filter the SnowEdge requests
    const filtered = snowEdgeRequests
      .filter((request) => {
        if (clientFilter === null) return true;
        
        const requestClientId = getClientIdFromRequest(request);
        console.log('Client filter check for request:', request.id, 'clientFilter:', clientFilter, 'requestClientId:', requestClientId);
        return requestClientId === clientFilter || String(requestClientId) === String(clientFilter);
      })
      .filter((request) => {
        if (statusFilter === null) return true;
        if (Array.isArray(statusFilter)) {
          console.log('Status filter check for request:', request.id, 'statusFilter:', statusFilter, 'request.status:', request.status);
          return statusFilter.includes(request.status);
        }
        return request.status === statusFilter;
      });
    
    console.log('SnowEdge request count after filtering:', filtered.length);
    
    // Then sort the filtered results
    return filtered.sort((a, b) => {
      let valueA, valueB;
      
      // Determine which fields to compare based on sortBy
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.created_at || 0);
          valueB = new Date(b.created_at || 0);
          break;
        case 'name':
          valueA = getClientNameFromRequest(a, clients) || '';
          valueB = getClientNameFromRequest(b, clients) || '';
          break;
        case 'status':
          valueA = a.status || '';
          valueB = b.status || '';
          break;
        default:
          // Default to date sorting
          valueA = new Date(a.created_at || 0);
          valueB = new Date(b.created_at || 0);
      }
      
      // Apply the sort direction
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      // Compare the values
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
  }, [snowEdgeRequests, clientFilter, statusFilter, sortBy, sortDirection, clients]);

  const renderAdminStaffContent = () => (
    <div className="admin-staff-content">
      {/* Secondary tabs for Regular/SnowEdge Requests */}
      <div className="secondary-tabs">
        <Tabs 
          defaultActiveKey="regular" 
          onChange={(key) => {
            const newRequestType = key === 'regular' ? 'regular' : 'snowedge';
            setRequestType(newRequestType);
            
            // Reset type filter when switching to SnowEdge (since it's not applicable)
            if (newRequestType === 'snowedge') {
              setTypeFilter(null);
            }
            
            // Set appropriate default status filters based on tab
            if (newRequestType === 'regular') {
              setStatusFilter(['pending', 'claimed', 'completed']);
            } else {
              setStatusFilter(['submitted', 'approved', 'claimed', 'completed']);
            }
          }}
        >
          <TabPane tab="Regular Requests" key="regular" />
          <TabPane tab="SnowEdge Requests" key="snowedge" />
        </Tabs>
      </div>

      {/* Filters */}
      <div className="filter-container">
        <Dropdown
          overlay={
            <Menu
              onClick={(e) => handleClientFilterChange(e.key)}
              selectedKeys={[clientFilter ? String(clientFilter) : 'all']}
            >
              <Menu.Item key="all">All Clients</Menu.Item>
              {clients.map((client) => (
                <Menu.Item key={String(client.client_id)}>
                  {client.client_name}
                </Menu.Item>
              ))}
            </Menu>
          }
          visible={clientDropdownVisible}
          onVisibleChange={setClientDropdownVisible}
          trigger={['click']}
        >
          <Button className="filter-button">
            <UserOutlined />
            <span className="text">Client</span> <span className="separator">|</span> <span className="value">{clientFilter ? 
              (clients.find(c => c.client_id === clientFilter || String(c.client_id) === String(clientFilter))?.client_name || 'All') 
              : 'All'}</span>
            <DownOutlined style={{ fontSize: '10px', marginLeft: '5px' }} />
          </Button>
        </Dropdown>
        
        <Dropdown
          overlay={
            <Menu
              onClick={(e) => {
                setTypeFilter(e.key === 'all' ? null : e.key);
                setTypeDropdownVisible(false);
              }}
              selectedKeys={[typeFilter]}
            >
              <Menu.Item key="all">All Types</Menu.Item>
              <Menu.Item key="salting_lots">Salting Lots</Menu.Item>
              <Menu.Item key="plowing_lots">Plowing Lots</Menu.Item>
              <Menu.Item key="emergency_exits">Emergency Exits</Menu.Item>
              <Menu.Item key="loading_docks">Loading Docks</Menu.Item>
              <Menu.Item key="walkways">Walkways</Menu.Item>
              <Menu.Item key="other">Other</Menu.Item>
            </Menu>
          }
          visible={typeDropdownVisible}
          onVisibleChange={setTypeDropdownVisible}
          trigger={['click']}
          style={{ display: requestType === 'regular' ? 'inline-block' : 'none' }}
        >
          <Button className="filter-button">
            <span className="text">Type</span> <span className="separator">|</span> <span className="value">{typeFilter ? getRequestTypeLabel(typeFilter) : 'All'}</span>
            <DownOutlined style={{ fontSize: '10px', marginLeft: '5px' }} />
          </Button>
        </Dropdown>
        
        {requestType === 'regular' && (
          <Dropdown
            overlay={
              <Menu
                onClick={(e) => {
                  const status = e.key === 'all' ? ['pending', 'claimed', 'completed'] : [e.key];
                  setStatusFilter(status);
                  setStatusDropdownVisible(false);
                }}
                selectedKeys={statusFilter && statusFilter.length === 1 ? statusFilter : []}
              >
                <Menu.Item key="all">All Statuses</Menu.Item>
                <Menu.Item key="pending">Pending</Menu.Item>
                <Menu.Item key="claimed">Claimed</Menu.Item>
                <Menu.Item key="completed">Completed</Menu.Item>
              </Menu>
            }
            visible={statusDropdownVisible}
            onVisibleChange={setStatusDropdownVisible}
            trigger={['click']}
          >
            <Button className="filter-button">
              <span className="text">Status</span> <span className="separator">|</span> <span className="value">{statusFilter && statusFilter.length === 1 ? statusFilter[0].charAt(0).toUpperCase() + statusFilter[0].slice(1) : 'All'}</span>
              <DownOutlined style={{ fontSize: '10px', marginLeft: '5px' }} />
            </Button>
          </Dropdown>
        )}
        
        {requestType === 'snowedge' && (
          <Dropdown
            overlay={
              <Menu
                onClick={(e) => {
                  const status = e.key === 'all' ? ['submitted', 'approved', 'claimed', 'completed'] : [e.key];
                  setStatusFilter(status);
                  setStatusDropdownVisible(false);
                }}
                selectedKeys={statusFilter && statusFilter.length === 1 ? statusFilter : []}
              >
                <Menu.Item key="all">All Statuses</Menu.Item>
                <Menu.Item key="submitted">Submitted</Menu.Item>
                <Menu.Item key="approved">Approved</Menu.Item>
                <Menu.Item key="claimed">Claimed</Menu.Item>
                <Menu.Item key="completed">Completed</Menu.Item>
              </Menu>
            }
            visible={statusDropdownVisible}
            onVisibleChange={setStatusDropdownVisible}
            trigger={['click']}
          >
            <Button className="filter-button">
              <span className="text">Status</span> <span className="separator">|</span> <span className="value">{statusFilter && statusFilter.length === 1 ? statusFilter[0].charAt(0).toUpperCase() + statusFilter[0].slice(1) : 'All'}</span>
              <DownOutlined style={{ fontSize: '10px', marginLeft: '5px' }} />
            </Button>
          </Dropdown>
        )}
        
        <Dropdown
          overlay={
            <Menu 
              onClick={(e) => handleSortChange(e.key)}
              selectedKeys={[sortBy]}
            >
              <Menu.Item key="date">Date</Menu.Item>
              <Menu.Item key="name">Name</Menu.Item>
              <Menu.Item key="status">Status</Menu.Item>
            </Menu>
          }
          visible={sortDropdownVisible}
          onVisibleChange={setSortDropdownVisible}
          trigger={['click']}
        >
          <Button className="filter-button">
            <span className="text">Sort by</span> <span className="separator">|</span> <span className="value">
              {sortBy === 'date' ? 'Date' : sortBy === 'name' ? 'Name' : sortBy === 'status' ? 'Status' : 'Date'}
              {' '}{sortDirection === 'asc' ? '↑' : '↓'}
            </span>
            <DownOutlined style={{ fontSize: '10px', marginLeft: '5px' }} />
          </Button>
        </Dropdown>
        
        <Button 
          icon={<FileTextOutlined />} 
          onClick={() => handleGenerateReport(clientFilter)} 
          loading={generatingReport}
          disabled={!clientFilter}
          className="generate-report-btn"
        >
          Generate Report for the Client
        </Button>
      </div>

      {/* Main content with request list and map */}
      <Row className="content-wrapper" style={{ width: '100%' }}>
        <Col span={17} className="custom-left-panel" style={{ height: '100%', overflowY: 'auto', paddingRight: '16px' }}>
          <List
            className="request-list"
            itemLayout="horizontal"
            dataSource={requestType === 'regular' ? filteredRequests : filteredSnowEdgeRequests}
            loading={requestsLoading}
            locale={{ emptyText: 'No matching requests found' }}
            renderItem={(request) => {
              // Debug the request structure for this specific item
              console.log('Rendering request item:', request.id, {
                clientEmail: request.client_email,
                clientName: getClientNameFromRequest(request, clients),
                siteName: request.site?.site_name || 'Unassigned'
              });
              
              // Create tooltip content
              const tooltipContent = (
                <div>
                  <div><strong>Site:</strong> {request.site?.site_name || 'Unassigned'}</div>
                  <div><strong>Address:</strong> {request.site?.address || 'No address'}</div>
                  <div><strong>Client:</strong> {getClientNameFromRequest(request, clients)}</div>
                  {requestType === 'snowedge' && (request.requested_time || request.end_time) && (
                    <div>
                      <div><strong>Start:</strong> {request.requested_date ? new Date(request.requested_date).toLocaleDateString() : ''}{request.requested_time ? ' ' + formatTimeHHMM(request.requested_time) : ''}</div>
                      {(request.end_date || request.end_time) && (
                        <div><strong>End:</strong> {request.end_date ? new Date(request.end_date).toLocaleDateString() : ''}{request.end_time ? ' ' + formatTimeHHMM(request.end_time) : ''}</div>
                      )}
                    </div>
                  )}
                  {requestType === 'snowedge' && request.notes && (
                    <div><strong>Notes:</strong> {request.notes.length > 50 ? `${request.notes.substring(0, 50)}...` : request.notes}</div>
                  )}
                </div>
              );
              
              return (
                <Tooltip title={tooltipContent} placement="right">
                  <List.Item
                    key={request.id}
                    onClick={() => handleRequestClick(request.id)}
                    className={selectedRequest && selectedRequest.id === request.id ? 'active-request' : ''}
                    style={{ cursor: 'pointer', borderLeft: selectedRequest && selectedRequest.id === request.id ? '4px solid #1890ff' : '4px solid transparent' }}
                  >
                    <div className="request-card">
                      <div className="request-card-left">
                        <div className="request-card-title">
                          {requestType === 'regular' ? request.address : (request.site && request.site.site_name) || 'No site specified'}
                          <span className={`request-tag ${requestType === 'regular' ? (request.request_type?.replace('_', '-') || '') : 'snowedge'}`}>
                            {requestType === 'regular' ? getRequestTypeLabel(request.request_type) : 'SnowEdge'}
                          </span>
                        </div>
                        <div className="request-card-info">
                          <div className="request-card-info-item">
                            <MailOutlined /> {request.client_email}
                          </div>
                          <div className="request-card-info-item">
                            <UserOutlined /> {getClientNameFromRequest(request, clients)}
                          </div>
                          <div className="request-card-info-item">
                            <EnvironmentOutlined /> {request.site && request.site.address ? request.site.address : 'No address specified'}
                          </div>
                          {requestType === 'snowedge' && (request.requested_time || request.end_time) && (
                            <div className="request-card-info-item time-range">
                              <ClockCircleOutlined /> 
                              <div className="time-container">
                                <div>
                                  <span className="time-label">Start: </span>
                                  <span className="time-value">
                                    {request.requested_date ? new Date(request.requested_date).toLocaleDateString() : ''} 
                                    {request.requested_time ? ' ' + formatTimeHHMM(request.requested_time) : ''}
                                  </span>
                                </div>
                                {(request.end_date || request.end_time) && (
                                  <div>
                                    <span className="time-label">End: </span>
                                    <span className="time-value">
                                      {request.end_date ? new Date(request.end_date).toLocaleDateString() : ''} 
                                      {request.end_time ? ' ' + formatTimeHHMM(request.end_time) : ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="request-card-right">
                        <div className="request-id">Request ID: {request.id}</div>
                        <div className="request-time">
                          {request.created_at ? new Date(request.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          }).replace(',', '') : 'No Date'}
                        </div>
                        <div className="request-status">
                          <span className={`status-dot ${request.status}`}></span>
                          <span className="status-text">
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </div>
                        {requestType === 'snowedge' && request.status === 'submitted' && (
                          <Button 
                            type="primary"
                            size="small"
                            className="approve-request-btn"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the list item click
                              handleApproveSnowEdgeRequest(request.id);
                            }}
                          >
                            Approve Request
                          </Button>
                        )}
                      </div>
                    </div>
                  </List.Item>
                </Tooltip>
              );
            }}
          />
        </Col>

        <Col span={7} className="custom-right-panel" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <MapContainer
            center={[43.6532, -79.3832]}
            zoom={12}
            whenCreated={setMap}
            style={{ height: '100%' }}
          >
            <TileLayer
              url={getMapboxUrl()}
              attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              maxZoom={19}
            />
            {(requestType === 'regular' ? filteredRequests : filteredSnowEdgeRequests).map((request) => {
              const lat = requestType === 'regular' ? request.latitude : (request.site && request.site.latitude);
              const lon = requestType === 'regular' ? request.longitude : (request.site && request.site.longitude);

              if (lat && lon) {
                // Use different marker color based on request type
                const markerIcon = requestType === 'regular' ? customMarkerIcon : 
                  L.icon({
                    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
                    shadowSize: [41, 41],
                    className: 'snowedge-marker' // Add this class for styling
                  });
                
                return (
                  <Marker
                    key={request.id}
                    position={[lat, lon]}
                    icon={markerIcon}
                    eventHandlers={{ click: () => handleRequestClick(request.id) }}
                  >
                    <Popup>
                      <div>
                        <strong>{requestType === 'regular' ? request.address : (request.site && request.site.site_name)}</strong>
                        <div>{request.site && request.site.address}</div>
                        {requestType === 'snowedge' && (request.requested_time || request.end_time) && (
                          <>
                            <div><strong>Start:</strong> {request.requested_date ? new Date(request.requested_date).toLocaleDateString() : ''}{request.requested_time ? ' ' + formatTimeHHMM(request.requested_time) : ''}</div>
                            {(request.end_date || request.end_time) && (
                              <div><strong>End:</strong> {request.end_date ? new Date(request.end_date).toLocaleDateString() : ''}{request.end_time ? ' ' + formatTimeHHMM(request.end_time) : ''}</div>
                            )}
                          </>
                        )}
                        <div><strong>Status:</strong> {request.status}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              }
              return null; // Skip rendering marker if coordinates are not available
            })}
          </MapContainer>
        </Col>
      </Row>
    </div>
  );

  const handleTabChange = (key) => {
    setLoading(true);
    setTimeout(() => {
      setActiveTab(key);
      setLoading(false);
    }, 500); // Adjust this delay as needed
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case '1':
        return <ClientRequestForm />;
      case '2':
        return <SnowEdgeRequestForm />;
      case '3':
        return <ClientRequestsPortal clientEmail={userEmail} />;
      default:
        return null;
    }
  };

  const handleClientFilterChange = (key) => {
    console.log('Client filter changed to:', key);
    const newFilter = key === 'all' ? null : key;
    console.log('Setting clientFilter to:', newFilter);
    setClientFilter(newFilter);
    setClientDropdownVisible(false);
    
    // Force redraw
    setTimeout(() => {
      console.log('Current clientFilter after change:', clientFilter);
    }, 100);
  };

  // Add a function to fetch the active dispatcher
  const fetchActiveDispatcher = async () => {
    try {
      const response = await api.get('/staff/');
      if (response.data) {
        const currentReceiver = response.data.find(staff => staff.is_request_receiver);
        if (currentReceiver) {
          setActiveDispatcher(currentReceiver);
          setSelectedRequestReceiver(currentReceiver.staff_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch active dispatcher:', error);
      // Don't show error message to user for this
    }
  };

  const handleSetActiveDispatcher = async (staffId) => {
    try {
      await api.post(`/staff/${staffId}/set_request_receiver/`);
      
      // Refresh the active dispatcher after setting
      fetchActiveDispatcher();
      message.success('Request receiver updated successfully');
    } catch (error) {
      console.error('Failed to update request receiver:', error);
      message.error('Failed to update request receiver');
    }
  };

  // Add time formatting function at the top level of the component
  const formatTimeHHMM = (timeString) => {
    if (!timeString) return '';
    // If time is in HH:MM:SS format, extract just HH:MM
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return timeString;
  };

  return (
    <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832", controlItemBgActive: "#E4F2EB" } }}>
      <Spin spinning={loading} tip="Loading...">
        <div className="requests-page">
          <div className="page-header">
            <Title level={3} style={{ margin: 0, color: '#333' }}>Requests</Title>
            {!isMobile && userRole !== 'client' && (
              <div className="active-dispatcher">
                <Text strong>Active Dispatcher:</Text>{' '}
                <Select
                  style={{ width: 200, marginLeft: '8px' }}
                  placeholder="Select dispatcher"
                  value={selectedRequestReceiver}
                  onChange={handleSetActiveDispatcher}
                  loading={!staffMembers.length}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase()) || false
                  }
                >
                  {staffMembers
                    .filter(staff => staff.work_email && staff.is_dispatcher)
                    .map(staff => (
                      <Option 
                        key={staff.staff_id} 
                        value={staff.staff_id}
                        label={`${staff.name} (${staff.work_email})`}
                      >
                        {staff.name} {staff.work_email ? `(${staff.work_email})` : ''}
                      </Option>
                    ))}
                </Select>
              </div>
            )}
          </div>
          
          <div className="tabs-header">
            {!isMobile ? (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
              >
                {userRole !== 'client' && (
                  <TabPane tab="View Requests" key="1">
                    {renderAdminStaffContent()}
                  </TabPane>
                )}
                {(userRole !== 'client' || hasRegularRequestAccess) && (
                  <TabPane tab="Submit Regular Request" key="2">
                    <ClientRequestForm />
                  </TabPane>
                )}
                {(userRole !== 'client' || hasSnowEdgeAccess) && (
                  <TabPane tab="Submit SnowEdge Request" key="3">
                    <SnowEdgeRequestForm />
                  </TabPane>
                )}
                <TabPane tab="Client Requests" key="4">
                  <ClientRequestsPortal clientEmail={userEmail} />
                </TabPane>
                {userRole !== 'client' && (
                  <TabPane tab="Dispatches" key="5">
                    <DispatchRequestsTable />
                  </TabPane>
                )}
              </Tabs>) : (
                <div style={{ width: '100%' }}>
                  <Select
                    value={activeTab}
                    onChange={handleTabChange}
                    style={{ width: '100%', marginBottom: '16px' }}
                    defaultValue={"1"}
                  >
                    <Select.Option value="1">Submit Regular Request</Select.Option>
                    {(userRole !== 'client' || hasSnowEdgeAccess) && (
                      <Select.Option value="2">Submit SnowEdge Request</Select.Option>
                    )}
                    <Select.Option value="3">Client Requests</Select.Option>
                  </Select>
                  <div>{renderTabContent()}</div>
                </div>
              )}
          </div>
        </div>
      </Spin>
      <Modal
        title={selectedRequest ? <Title level={4}>{requestType === 'regular' ? `Regular Request #${selectedRequest.id}` : `SnowEdge Request #${selectedRequest.id}`}</Title> : <Title level={4}>Request Details</Title>}
        open={viewRequestOpen}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnClose
        width={1000}
        className="request-detail-modal"
      >
        {selectedRequest && (
          <>
            <Row gutter={[24, 24]}>
              {/* Left Column - Request Details */}
              <Col span={10}>
                <div className="request-detail-section">
                  <h3>Request Details</h3>
                  
                  <div className="detail-row">
                    <div className="detail-item">
                      <span className="detail-label"><CheckCircleOutlined style={{ color: '#888' }} /> Status</span>
                      <span className="detail-value request-status">
                        <span className={`status-dot ${selectedRequest.status}`}></span>
                        <span className="status-text">
                          {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                        </span>
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="detail-label"><InfoCircleOutlined /> Type</span>
                      <span className="detail-value">
                        <span className={`request-tag ${requestType === 'regular' ? (selectedRequest.request_type?.replace('_', '-') || '') : 'snowedge'}`}>
                          {requestType === 'regular' ? getRequestTypeLabel(selectedRequest.request_type) : 'SnowEdge'}
                        </span>
                      </span>
                    </div>
                  </div>
                  
                  <div className="detail-row">
                    <div className="detail-item full-width form-style">
                      <span className="detail-label"><CalendarOutlined /> Created</span>
                      <span className="detail-value">
                        {selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        }) : 'No Date'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="detail-row">
                    <div className="detail-item form-style">
                      <span className="detail-label"><UserOutlined /> Client</span>
                      <span className="detail-value">{getClientNameFromRequest(selectedRequest, clients)}</span>
                    </div>
                  
                    <div className="detail-item form-style">
                      <span className="detail-label"><MailOutlined /> Client Email</span>
                      <span className="detail-value">{selectedRequest.client_email}</span>
                    </div>
                  </div>
                  
                  {selectedRequest.contact_name && (
                    <div className="detail-row">
                      <div className="detail-item form-style">
                        <span className="detail-label"><UserOutlined /> Contact Name</span>
                        <span className="detail-value">{selectedRequest.contact_name}</span>
                      </div>
                  
                      {selectedRequest.contact_phone && (
                        <div className="detail-item form-style">
                          <span className="detail-label"><PhoneOutlined /> Contact Phone</span>
                          <span className="detail-value">{selectedRequest.contact_phone}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="detail-row">
                    <div className="detail-item form-style">
                      <span className="detail-label"><EnvironmentOutlined /> Site</span>
                      <span className="detail-value">
                        {selectedRequest.site?.site_name || (requestType === 'regular' ? selectedRequest.address : 'Unassigned')}
                      </span>
                    </div>
                  
                    <div className="detail-item form-style">
                      <span className="detail-label"><EnvironmentOutlined /> Location</span>
                      <span className="detail-value">
                        {selectedRequest.site?.address || (requestType === 'regular' ? selectedRequest.address : 'No address specified')}
                      </span>
                    </div>
                  </div>
                  
                  {requestType === 'regular' && selectedRequest.latitude && selectedRequest.longitude && (
                    <div className="detail-row">
                      <div className="detail-item form-style full-width">
                        <span className="detail-label"><EnvironmentOutlined /> Coordinates</span>
                        <span className="detail-value">
                          {selectedRequest.latitude}, {selectedRequest.longitude}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="detail-row">
                    <div className="detail-item form-style full-width">
                      <span className="detail-label"><InfoCircleOutlined /> Details</span>
                      <div className="detail-value detail-text">
                        {selectedRequest.details || 'No details provided'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="map-container">
                    <p className="detail-label">Click on the map to pin point location</p>
                    <RequestLocationMap request={selectedRequest} requestType={requestType} />
                  </div>
                </div>
              </Col>
              
              {/* Right Column - Status Updates */}
              <Col span={14}>
                <div className="request-status-section">
                  <h3>Status Updates</h3>
                  
                  {/* Claim Notes Section */}
                  <div className="status-notes">
                    <Text strong className="claim-notes-label">
                      <Badge status="processing" /> Claim Notes
                    </Text>
                    {selectedRequest.status === 'pending' ? (
                      <>
                        <Input.TextArea
                          rows={3}
                          value={claimNotes}
                          onChange={(e) => setClaimNotes(e.target.value)}
                          placeholder="Enter claim notes..."
                          style={{ marginTop: '4px', borderRadius: '4px', marginBottom: '50px' }}
                        />
                        
                        {/* Claim Request Button - Moved here */}
                        {requestType === 'regular' && (
                          <Button
                            type="primary"
                            onClick={(e) => handleClaimRequest(e, selectedRequest.id)}
                            disabled={selectedRequest.status !== 'pending'}
                            className="claim-request-btn"
                          >
                            Claim Request
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="notes-display">
                        {selectedRequest.claimed_notes || 'No claim notes available.'}
                      </div>
                    )}
                  </div>
                  
                  {/* Completion Notes Section */}
                  <div className="status-notes">
                    <Text strong className="completion-notes-label">
                      <Badge status="success" /> Completion Notes
                    </Text>
                    {selectedRequest.status === 'pending' ? (
                      <div className="disabled-notes">
                        No completion notes available. Claim Request first.
                      </div>
                    ) : selectedRequest.status !== 'completed' ? (
                      <>
                        <Input.TextArea
                          rows={3}
                          value={completeNotes}
                          onChange={(e) => setCompleteNotes(e.target.value)}
                          placeholder="Enter completion notes..."
                          style={{ marginTop: '4px', borderRadius: '4px', marginBottom: '50px' }}
                          maxLength={350}
                        />
                        
                        {/* Complete Request Button - Moved here */}
                        {requestType === 'regular' && selectedRequest.status === 'claimed' && (
                          <Button
                            type="primary"
                            onClick={(e) => handleCompleteRequest(e, selectedRequest.id)}
                            disabled={selectedRequest.status === 'completed'}
                            className="complete-request-btn"
                          >
                            Complete Request
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="notes-display">
                        {selectedRequest.completed_notes || 'No completion notes available.'}
                      </div>
                    )}
                  </div>
                  
                  {/* Pictures Section */}
                  <div className="pictures-section">
                    <h3><PictureOutlined /> Pictures</h3>
                    
                    {openRequest && openRequest.image_urls && openRequest.image_urls.length > 0 ? (
                      <div className="image-grid">
                        {openRequest.image_urls.map((image_url, index) => (
                          <Image
                            key={`${image_url}-${index}-${imageKey}`}
                            src={image_url}
                            alt={`Request image ${index + 1}`}
                            style={{ width: '100%', marginBottom: 8, objectFit: 'contain' }}
                          />
                        ))}
                      </div>
                    ) : (
                      <Empty description="No images uploaded" style={{ margin: '16px 0' }} />
                    )}
                    
                    <div className="upload-section">
                      <Upload {...props} listType="picture">
                        <Button icon={<UploadOutlined />}>Select File</Button>
                      </Upload>
                      <Button
                        type="primary"
                        onClick={handleUpload}
                        disabled={fileList.length === 0}
                        loading={uploading}
                        style={{ marginTop: 16 }}
                      >
                        {uploading ? 'Uploading' : 'Start Upload'}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            
            {/* Dispatch Section */}
            {selectedRequest.status === 'claimed' && (
              <Collapse 
                className="dispatch-collapse"
                bordered={false}
              >
                <Collapse.Panel header="Dispatch Request" key="dispatch">
                  <div className="dispatch-section">
                    {activeDispatches.length > 0 ? (
                      <div className="dispatch-list">
                        <Row gutter={16} className="dispatch-stats">
                          <Col span={8}>
                            <Statistic 
                              title="Total Dispatches" 
                              value={activeDispatches.length}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic 
                              title="In Progress" 
                              value={activeDispatches.filter(d => ['unread', 'read', 'confirmed'].includes(d.status)).length} 
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic 
                              title="Completed" 
                              value={activeDispatches.filter(d => d.status === 'completed').length} 
                            />
                          </Col>
                        </Row>
                        
                        <List
                          dataSource={activeDispatches}
                          renderItem={dispatch => (
                            <List.Item 
                              actions={[
                                dispatch.status !== 'completed' && (
                                  <Button 
                                    size="small"
                                    onClick={(e) => handleCompleteDispatch(e, dispatch.id)}
                                  >
                                    Complete
                                  </Button>
                                )
                              ].filter(Boolean)}
                            >
                              <List.Item.Meta
                                avatar={
                                  <Badge 
                                    status={getDispatchStatusColor(dispatch.status)} 
                                    text={`${dispatch.status.charAt(0).toUpperCase()}${dispatch.status.slice(1)}`} 
                                  />
                                }
                                title={`${dispatch.staff_name || 'Staff'}`}
                                description={
                                  <>
                                    <div>{dispatch.notes}</div>
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                      Created: {moment(dispatch.created_at).format('MMM D, YYYY h:mm A')}
                                    </div>
                                  </>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    ) : (
                      <Empty description="No dispatches yet" />
                    )}
                    
                    {/* Add New Dispatch section */}
                    <div className="new-dispatch-section">
                      <h4>Add New Dispatch</h4>
                      <Form
                        form={form}
                        layout="vertical"
                      >
                        <Form.Item label="Select Staff" name="staffId" rules={[{ required: true, message: 'Please select a staff member' }]}>
                          <Select
                            placeholder="Search staff..."
                            showSearch
                            filterOption={(input, option) =>
                              option?.label?.toLowerCase().includes(input.toLowerCase()) || false
                            }
                            searchValue={staffSearchValue}
                            onSearch={staffSearch}
                          >
                            {filteredStaff.map(staff => (
                              <Select.Option key={staff.staff_id} value={staff.staff_id}>
                                {staff.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item label="Notes" name="notes">
                          <Input.TextArea
                            rows={3}
                            value={dispatchNotes}
                            onChange={e => setDispatchNotes(e.target.value)}
                            placeholder="Add notes for the staff member..."
                          />
                        </Form.Item>
                        <Button 
                          type="primary"
                          onClick={() => {
                            const staffId = form.getFieldValue('staffId');
                            if (staffId) {
                              handleDispatchRequest(staffId);
                            } else {
                              message.error('Please select a staff member');
                            }
                          }}
                          loading={loadingDispatch}
                        >
                          Dispatch Request
                        </Button>
                      </Form>
                    </div>
                  </div>
                </Collapse.Panel>
              </Collapse>
            )}
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default RequestsPage;